import { useField } from 'formik';
import { useMemo } from 'react';
import ReactSelect, { GroupBase, Props } from 'react-select';

import { Error } from './Error';
import { Info } from './Info';

export type SelectProps<
    Option,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
> = Props<Option, IsMulti, Group> & {
    label: string;
    name: string;
    showLabel?: boolean;
    info?: string;
    search?: boolean;
};

export const Select = <Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>({
    showLabel = true,
    label,
    name,
    id,
    onChange,
    value,
    isMulti,
    isSearchable = false,
    info,
    options,
    search = false,
    ...props
}: SelectProps<Option, IsMulti, Group>) => {
    const [field, meta] = useField(name);

    const showError = useMemo(() => !!meta.error && meta.touched, [meta.error, meta.touched]);
    const valid = useMemo(() => !meta.error && field.value, [meta.error, field.value]);

    return (
        <div className="space-y-2 text-left">
            <div className="relative">
                <label
                    htmlFor={name}
                    className={`absolute ${
                        search ? 'left-10' : 'left-3 top-2'
                    } transform transition-all duration-150 ease-in-out pointer-events-none
                    text-xs text-neutral-400 ${!showLabel && 'sr-only'}`}
                >
                    {label}
                </label>
                <ReactSelect
                    {...props}
                    unstyled
                    id={id}
                    name={name}
                    classNames={{
                        control: (state) =>
                            `${search ? `input-search pl-10 pr-3` : 'px-3'} ${!showLabel ? 'py-3' : 'pt-6 pb-3'} ${
                                state.menuIsOpen ? 'rounded-t-lg border border-b-transparent' : 'rounded-lg border'
                            } ${showError ? 'border-danger-300' : valid ? 'border-green-300' : 'border-neutral-400'}`,
                        menuList: () =>
                            `-mt-px bg-white border rounded-b-lg z-10 ${
                                showError ? 'border-danger-300' : valid ? 'border-green-300 ' : 'border-neutral-400'
                            }`,
                        option: (state) =>
                            `px-3 py-3 transition-colors duration-150 ease-in-out
                            ${state.isSelected && `bg-green-100 ${state.selectProps.isMulti && 'option-ticked'}`}
                            ${state.isDisabled ? `text-neutral-300` : `text-neutral-500`}
                            ${!state.isDisabled && 'hover:bg-green-100'}`,
                        indicatorsContainer: () => `transform ${!showLabel ? 'translate-y-0' : '-translate-y-1.5'}`,
                        dropdownIndicator: (state) =>
                            `text-neutral-500 transform !transition-all !duration-150 ease-in-out ${
                                state.selectProps.menuIsOpen ? 'rotate-180' : 'rotate-0'
                            }`,
                        valueContainer: () => 'gap-1',
                        multiValue: () => 'bg-blue-500 text-sm px-1.5 py-px text-white rounded',
                        noOptionsMessage: () => 'py-2',
                    }}
                    options={options}
                    onChange={onChange}
                    isMulti={isMulti}
                    isSearchable={isSearchable}
                    hideSelectedOptions={false}
                    value={value}
                />
            </div>
            {showError && <Error>{meta.error}</Error>}
            {info && <Info info={info} />}
        </div>
    );
};
